import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import { useEffect, useState, useCallback } from "react";
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Link2,
    Highlighter,
    Type as TypeIcon,
    Undo,
    Redo,
    Palette,
    BadgeEuro,
} from "lucide-react";

/**
 * Marken-konformer Rich-Text-Editor auf TipTap-Basis.
 *
 * Bewusst eng begrenzt (kein freies HTML, keine freien Farben, keine Bilder,
 * keine Tabellen). Alles was hier eingegeben wird landet nach Server-Sanitize
 * unverändert im DB-Feld und wird vom `<RichText>`-Renderer sicher ausgegeben.
 *
 * Erlaubte Inline-Formatierungen:
 *   - bold / italic / underline
 *   - Marken-Farbe (4 Presets)
 *   - Highlight (2 Presets)
 *   - Text-Größe (3 Presets)
 *   - Link (öffnet in neuem Tab bei externen URLs)
 */

/* ---------------------------------------------------------------------------
 * Marken-Palette (KEINE freie RGB-Auswahl).
 *
 * WICHTIG: Diese hex-Werte MÜSSEN synchron zu den CSS-Variablen in
 * `/app/frontend/src/index.css` (`--hf-gold`, `--hf-gold-dark`, `--hf-ink`,
 * `--hf-muted`, `--hf-gold-soft`) bleiben — TipTap kann nur hex-Strings setzen,
 * deshalb hier hardcoded. Bei Marken-Palette-Änderung: index.css UND diese
 * Konstanten anpassen.
 *
 * Farb-Semantik:
 *  - Ink   `#1D1B18` — Default-Text (dunkles Anthrazit)
 *  - Gold  `#C9A961` — Marken-Gold, identisch zum CTA-Button-BG (`--hf-gold`)
 *  - Bronze`#A88A4A` — Gold-Dark, dunklerer Akzent für Fließtext (`--hf-gold-dark`)
 *  - Grau  `#716B63` — Ink-Muted, sekundärer Text
 * ------------------------------------------------------------------------- */
export const RT_COLORS: Array<{ label: string; hex: string }> = [
    { label: "Ink", hex: "#1D1B18" },
    { label: "Gold", hex: "#C9A961" },
    { label: "Bronze", hex: "#A88A4A" },
    { label: "Grau", hex: "#716B63" },
];

/* Highlight-Presets — Farb-Kombis identisch zum CTA-Button-Styling
 * (`hf-btn-primary`: bg=Gold, color=Ink), damit visuelle Kohärenz entsteht.
 *
 * Gold-Marker : bg=#C9A961 (--hf-gold), fg=#1D1B18 (--hf-ink)
 * Sand-Marker : bg=#E7D6A8 (--hf-gold-soft), fg=#1D1B18 (--hf-ink)
 */
export const RT_HIGHLIGHTS: Array<{
    label: string;
    bg: string;
    fg: string;
}> = [
    { label: "Gold-Marker", bg: "#C9A961", fg: "#1D1B18" },
    { label: "Sand-Marker", bg: "#E7D6A8", fg: "#1D1B18" },
];

export const RT_SIZES: Array<{ label: string; em: string }> = [
    { label: "S", em: "0.875em" },
    { label: "M", em: "1em" },
    { label: "L", em: "1.25em" },
];

/* ---------------------------------------------------------------------------
 * TipTap 3.x: `TextStyle` und `FontSize` sind bereits im
 * `@tiptap/extension-text-style`-Paket integriert — kein custom Extend nötig.
 * ------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
 * Toolbar-Icon-Button.
 * ------------------------------------------------------------------------- */
function TBBtn({
    onClick,
    active,
    disabled,
    title,
    children,
    testId,
}: {
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
    testId?: string;
}) {
    return (
        <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            disabled={disabled}
            title={title}
            aria-label={title}
            aria-pressed={active}
            data-testid={testId}
            className={`
                inline-flex items-center justify-center h-8 w-8 rounded-sm
                transition-colors
                ${active ? "bg-hf-gold/20 text-hf-gold" : "text-hf-ink hover:bg-hf-gold/10"}
                disabled:opacity-40 disabled:cursor-not-allowed
            `}
        >
            {children}
        </button>
    );
}

function TBSep() {
    return (
        <span
            aria-hidden
            className="mx-1 h-5 w-px self-center bg-hf-line"
        />
    );
}

/* ---------------------------------------------------------------------------
 * Toolbar.
 * ------------------------------------------------------------------------- */
function Toolbar({ editor, testIdPrefix }: { editor: Editor; testIdPrefix: string }) {
    const [colorOpen, setColorOpen] = useState(false);
    const [hiOpen, setHiOpen] = useState(false);
    const [sizeOpen, setSizeOpen] = useState(false);

    const setLink = useCallback(() => {
        const prev = editor.getAttributes("link").href || "";
        const url = window.prompt("Link-URL (leer lassen zum Entfernen):", prev);
        if (url === null) return; // Cancel
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        const isExternal = /^https?:\/\//i.test(url);
        editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({
                href: url,
                target: isExternal ? "_blank" : null,
                rel: isExternal ? "noopener noreferrer" : null,
            })
            .run();
    }, [editor]);

    /**
     * Preis-Stempel-Snippet einfügen: fettes „ab X €" mit Gold-Highlight.
     *
     * Der Platzhalter „X" wird automatisch selektiert, damit der User sofort
     * die Zahl eintippen kann ohne extra Klick. Zwischen Zahl und Währung
     * ein non-breaking space (\u00A0), damit „199 €" nie zwischen Zeilen
     * umbricht.
     */
    const insertPriceStamp = useCallback(() => {
        const from = editor.state.selection.from;
        const stamp = "ab X\u00A0€";
        editor
            .chain()
            .focus()
            .setMark("bold")
            .setMark("highlight", { color: "#C9A961" })
            .setColor("#1D1B18")
            .insertContent(stamp)
            // Marks nach Insert wieder abschalten — Cursor nach dem Stempel
            // schreibt wieder normalen Fließtext (falls User weiter tippt).
            .unsetMark("highlight")
            .unsetMark("bold")
            .unsetColor()
            // „X" (Position 3–4 im Stamp) selektieren, damit User es
            // sofort mit der Preiszahl überschreiben kann.
            .setTextSelection({ from: from + 3, to: from + 4 })
            .run();
    }, [editor]);

    return (
        <div
            className="flex flex-wrap items-center gap-0.5 rounded-t-sm border border-hf-line bg-hf-surface px-1.5 py-1"
            style={{ background: "hsl(var(--hf-surface))" }}
            data-testid={`${testIdPrefix}-toolbar`}
        >
            <TBBtn
                onClick={() => editor.chain().focus().toggleBold().run()}
                active={editor.isActive("bold")}
                title="Fett (Strg+B)"
                testId={`${testIdPrefix}-bold`}
            >
                <Bold size={15} />
            </TBBtn>
            <TBBtn
                onClick={() => editor.chain().focus().toggleItalic().run()}
                active={editor.isActive("italic")}
                title="Kursiv (Strg+I)"
                testId={`${testIdPrefix}-italic`}
            >
                <Italic size={15} />
            </TBBtn>
            <TBBtn
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                active={editor.isActive("underline")}
                title="Unterstrichen (Strg+U)"
                testId={`${testIdPrefix}-underline`}
            >
                <UnderlineIcon size={15} />
            </TBBtn>

            <TBSep />

            {/* Farbe (Marken-Palette) */}
            <div className="relative">
                <TBBtn
                    onClick={() => {
                        setColorOpen((v) => !v);
                        setHiOpen(false);
                        setSizeOpen(false);
                    }}
                    active={colorOpen || !!editor.getAttributes("textStyle").color}
                    title="Textfarbe"
                    testId={`${testIdPrefix}-color`}
                >
                    <Palette size={15} />
                </TBBtn>
                {colorOpen && (
                    <div
                        className="absolute z-30 mt-1 flex gap-1 rounded-sm border border-hf-line bg-hf-bg p-1.5 shadow-lg"
                        style={{ background: "hsl(var(--hf-bg))" }}
                    >
                        {RT_COLORS.map((c) => (
                            <button
                                key={c.hex}
                                type="button"
                                title={c.label}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    editor.chain().focus().setColor(c.hex).run();
                                    setColorOpen(false);
                                }}
                                className="h-6 w-6 rounded-full border border-hf-line hover:scale-110 transition-transform"
                                style={{ background: c.hex }}
                                data-testid={`${testIdPrefix}-color-${c.label.toLowerCase()}`}
                            />
                        ))}
                        <button
                            type="button"
                            title="Farbe entfernen"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                editor.chain().focus().unsetColor().run();
                                setColorOpen(false);
                            }}
                            className="h-6 px-2 text-[10px] uppercase tracking-wider text-hf-muted hover:text-hf-ink"
                        >
                            ×
                        </button>
                    </div>
                )}
            </div>

            {/* Highlight */}
            <div className="relative">
                <TBBtn
                    onClick={() => {
                        setHiOpen((v) => !v);
                        setColorOpen(false);
                        setSizeOpen(false);
                    }}
                    active={hiOpen || editor.isActive("highlight")}
                    title="Highlight"
                    testId={`${testIdPrefix}-highlight`}
                >
                    <Highlighter size={15} />
                </TBBtn>
                {hiOpen && (
                    <div
                        className="absolute z-30 mt-1 flex gap-1 rounded-sm border border-hf-line bg-hf-bg p-1.5 shadow-lg whitespace-nowrap"
                        style={{ background: "hsl(var(--hf-bg))" }}
                    >
                        {RT_HIGHLIGHTS.map((h) => (
                            <button
                                key={h.bg}
                                type="button"
                                title={h.label}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    // Highlight-Farbe UND Textfarbe kombiniert
                                    editor
                                        .chain()
                                        .focus()
                                        .setHighlight({ color: h.bg })
                                        .setColor(h.fg)
                                        .run();
                                    setHiOpen(false);
                                }}
                                className="h-6 px-2 text-[11px] font-semibold rounded-sm border border-hf-line"
                                style={{ background: h.bg, color: h.fg }}
                                data-testid={`${testIdPrefix}-highlight-${h.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            >
                                {h.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            title="Highlight entfernen"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                editor.chain().focus().unsetHighlight().run();
                                setHiOpen(false);
                            }}
                            className="h-6 px-2 text-[10px] uppercase tracking-wider text-hf-muted hover:text-hf-ink"
                        >
                            ×
                        </button>
                    </div>
                )}
            </div>

            {/* Text-Größe */}
            <div className="relative">
                <TBBtn
                    onClick={() => {
                        setSizeOpen((v) => !v);
                        setColorOpen(false);
                        setHiOpen(false);
                    }}
                    active={sizeOpen}
                    title="Text-Größe"
                    testId={`${testIdPrefix}-size`}
                >
                    <TypeIcon size={15} />
                </TBBtn>
                {sizeOpen && (
                    <div
                        className="absolute z-30 mt-1 flex gap-1 rounded-sm border border-hf-line bg-hf-bg p-1.5 shadow-lg"
                        style={{ background: "hsl(var(--hf-bg))" }}
                    >
                        {RT_SIZES.map((s) => (
                            <button
                                key={s.label}
                                type="button"
                                title={`${s.label} (${s.em})`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    editor
                                        .chain()
                                        .focus()
                                        .setFontSize(s.em)
                                        .run();
                                    setSizeOpen(false);
                                }}
                                className="h-6 w-8 rounded-sm border border-hf-line text-[12px] font-semibold text-hf-ink hover:bg-hf-gold/10"
                                data-testid={`${testIdPrefix}-size-${s.label.toLowerCase()}`}
                            >
                                {s.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            title="Größe zurücksetzen"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                editor.chain().focus().unsetFontSize().run();
                                setSizeOpen(false);
                            }}
                            className="h-6 px-2 text-[10px] uppercase tracking-wider text-hf-muted hover:text-hf-ink"
                        >
                            ×
                        </button>
                    </div>
                )}
            </div>

            <TBSep />

            <TBBtn
                onClick={setLink}
                active={editor.isActive("link")}
                title="Link setzen (leer = entfernen)"
                testId={`${testIdPrefix}-link`}
            >
                <Link2 size={15} />
            </TBBtn>

            <TBSep />

            <TBBtn
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                title="Rückgängig (Strg+Z)"
                testId={`${testIdPrefix}-undo`}
            >
                <Undo size={15} />
            </TBBtn>
            <TBBtn
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                title="Wiederherstellen (Strg+Y)"
                testId={`${testIdPrefix}-redo`}
            >
                <Redo size={15} />
            </TBBtn>

            <TBSep />

            {/* Snippet-Bereich (rechts abgesetzt): Editorial-Fertig-Bausteine
                für wiederkehrende Passagen. Aktuell: Preis-Stempel. */}
            <TBBtn
                onClick={insertPriceStamp}
                title="Preis-Stempel einfügen (Gold-Highlight + Fett)"
                testId={`${testIdPrefix}-price-stamp`}
            >
                <BadgeEuro size={15} />
            </TBBtn>
        </div>
    );
}

/* ---------------------------------------------------------------------------
 * RichTextField (drop-in Ersatz für TextareaField).
 * ------------------------------------------------------------------------- */
type Props = {
    label: string;
    value: string;
    onChange: (html: string) => void;
    testId?: string;
    helpText?: string;
    /** min-height in Zeilen (Default 4) */
    rows?: number;
};

export default function RichTextField({
    label,
    value,
    onChange,
    testId = "rt-field",
    helpText,
    rows = 4,
}: Props) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Marken-Vorgabe: KEINE Listen, Blockquotes, Code-Blocks, etc.
                bulletList: false,
                orderedList: false,
                listItem: false,
                blockquote: false,
                codeBlock: false,
                heading: false,
                horizontalRule: false,
                strike: false,
                code: false,
            }),
            Underline,
            TextStyle,
            FontSize,
            Color,
            Highlight.configure({ multicolor: true }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: {
                    class: "hf-rt-link",
                },
            }),
        ],
        content: value || "",
        // Wichtig: `immediatelyRender: false` verhindert SSR-Hydration-Warnings.
        immediatelyRender: false,
        editorProps: {
            attributes: {
                class: "hf-rt-editor prose prose-sm max-w-none focus:outline-none",
                spellcheck: "true",
                "data-testid": `${testId}-editor`,
            },
        },
        onUpdate: ({ editor: ed }) => {
            const html = ed.getHTML();
            // TipTap gibt "<p></p>" für leeren Editor → als leerer String
            // durchreichen, damit Speicherverhalten identisch zu TextareaField.
            const isEmpty = html === "<p></p>" || html === "";
            onChange(isEmpty ? "" : html);
        },
    });

    // Externe value-Änderungen (z.B. Reload nach Save, Sektion-Wechsel) in
    // Editor spiegeln — nur wenn sie sich vom aktuellen HTML unterscheiden,
    // sonst würde der Cursor bei jedem Keystroke springen.
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const incoming = value || "";
        // Normalisierung: leerer String === "<p></p>"
        const normCurrent = current === "<p></p>" ? "" : current;
        if (normCurrent === incoming) return;
        editor.commands.setContent(incoming || "", { emitUpdate: false });
    }, [value, editor]);

    if (!editor) {
        return (
            <div className="flex flex-col gap-1" data-testid={testId}>
                <label className="text-xs uppercase tracking-wider text-hf-muted">
                    {label}
                </label>
                <div className="h-24 rounded-sm border border-hf-line bg-hf-surface" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1" data-testid={testId}>
            <label className="text-xs uppercase tracking-wider text-hf-muted">
                {label}
            </label>
            <Toolbar editor={editor} testIdPrefix={testId} />
            <div
                className="rounded-b-sm border border-hf-line border-t-0 bg-hf-bg px-3 py-2"
                style={{
                    background: "hsl(var(--hf-bg))",
                    minHeight: `${Math.max(1, rows) * 1.6 + 0.75}em`,
                }}
                data-testid={`${testId}-shell`}
            >
                <EditorContent editor={editor} />
            </div>
            {helpText && (
                <p className="text-xs text-hf-muted">{helpText}</p>
            )}
        </div>
    );
}
